import { useEffect, useRef, useState } from 'react';
import type { ViewId } from '../state';
import { api, ApiError, newIdempotencyKey } from '../lib/api';

type Props = {
    onNavigate: (id: ViewId) => void;
    balance: number;
    /** Apply the server-decided new balance after the spin lands. The
     *  result is committed server-side at click time, so even if the user
     *  closes the app before this fires the balance is already updated. */
    onSettled: (newBalance: number, won: boolean, payout: number) => void;
};

/**
 * Slot machine that takes the entire balance. Settlement is server-side
 * via /api/g667/spin: by the time the network call resolves the balance
 * is gone (or paid out 25x). The animation just visualises whichever
 * outcome the backend already picked.
 *
 * Exit-during-animation exploit is closed: clicking PLAY commits the
 * stake before any visual effect runs.
 */
export function Game667View({ onNavigate, balance, onSettled }: Props) {
    const [phase, setPhase] = useState<'idle' | 'spinning' | 'hell' | 'over'>('idle');
    const [outcome, setOutcome] = useState<'won' | 'lost' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const stripRefs = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)];
    const timeouts = useRef<number[]>([]);

    useEffect(
        () => () => {
            timeouts.current.forEach((id) => window.clearTimeout(id));
        },
        [],
    );

    const spin = async () => {
        if (balance <= 0 || (phase !== 'idle' && phase !== 'over')) return;
        setError(null);
        // Lock the UI BEFORE the network call so the user can't double-click.
        setPhase('spinning');

        // Backend commits the spin atomically. If it errors out we never
        // play the animation — the balance wasn't touched.
        let won = false;
        let payout = 0;
        let newBalance = balance;
        try {
            const r = await api.g667Spin(newIdempotencyKey());
            won = r.won;
            payout = r.payout;
            newBalance = r.balance;
        } catch (e) {
            setPhase('idle');
            setError(e instanceof ApiError
                ? (e.code === 'no_balance' ? 'Недостаточно BSP.' : `${e.status}: ${e.code}`)
                : 'Не удалось крутануть.');
            return;
        }

        const finalDigit = won ? 7 : 6;

        stripRefs.forEach((ref, idx) => {
            const el = ref.current;
            if (!el) return;
            const itemCount = 30 + idx * 15;
            const html: string[] = [`<div class="slot-item color-dash">-</div>`];
            for (let i = 0; i < itemCount; i++) {
                const num = Math.random() > 0.5 ? 6 : 7;
                html.push(`<div class="slot-item color-${num}">${num}</div>`);
            }
            html.push(`<div class="slot-item final-item color-${finalDigit}">${finalDigit}</div>`);
            el.innerHTML = html.join('');
            el.style.transition = 'none';
            el.style.transform = 'translateY(0)';
            void el.offsetWidth;
            const duration = 5 + idx;
            el.style.transition = `transform ${duration}s cubic-bezier(0.15, 0.85, 0.15, 1)`;
            el.style.transform = `translateY(-${(itemCount + 1) * 100}px)`;
        });

        // Mid-spin: on a loss flip middle digit from 7 → 6 with the
        // shake/red effect for drama. On a win all three already landed
        // on 7 so no flip is needed.
        const tHell = window.setTimeout(() => {
            if (won) return;
            setPhase('hell');
            stripRefs.forEach((ref) => {
                const el = ref.current;
                if (!el) return;
                el.parentElement?.classList.add('shake');
                const last = el.querySelector('.final-item') as HTMLDivElement | null;
                if (last) {
                    last.classList.remove('color-7');
                    last.classList.add('color-6');
                    last.textContent = '6';
                }
            });
        }, 7500);
        timeouts.current.push(tHell);

        const tOver = window.setTimeout(() => {
            stripRefs.forEach((ref) => ref.current?.parentElement?.classList.remove('shake'));
            setPhase('over');
            setOutcome(won ? 'won' : 'lost');
            onSettled(newBalance, won, payout);
        }, 10_500);
        timeouts.current.push(tOver);
    };

    return (
        <div className="view">
            <div className="title-667">667</div>

            <div className="slots-container">
                {stripRefs.map((ref, i) => (
                    <div key={i} className="slot-window">
                        <div className="slot-strip" ref={ref}>
                            <div className="slot-item color-dash">-</div>
                        </div>
                    </div>
                ))}
            </div>

            <div
                className="info-card"
                style={{
                    borderColor: 'var(--red)',
                    background: 'rgba(255, 77, 77, 0.05)',
                    textAlign: 'center',
                    padding: 12,
                }}
            >
                <p className="popup-text">
                    <b style={{ color: '#fff' }}>Цель игры: нужно выбить 777.</b>
                </p>
                <p className="text-red fw-900 f-12">
                    ⚠️ ВЫ ИГРАЕТЕ НА ВЕСЬ СВОЙ БАЛАНС.
                </p>
                <p className="popup-text">
                    Но в случае победы вы получаете <b>x25</b>. Шанс победить 1 к 25.
                </p>
                <p className="popup-text">
                    Чем больше баланс, тем больше шанс <i>(шаг 1 000 BSP = +1 шанс / до 5 к 25)</i>.
                </p>
            </div>

            {error && (
                <div className="action-result err">{error}</div>
            )}

            {phase !== 'spinning' && phase !== 'hell' && (
                <button
                    type="button"
                    className="action-btn btn-red"
                    style={{ padding: 16, fontSize: 16 }}
                    onClick={spin}
                    disabled={balance <= 0}
                >
                    {phase === 'over' ? 'ИГРАТЬ СНОВА' : 'ИГРАТЬ'}
                </button>
            )}
            {phase === 'over' && outcome === 'lost' && (
                <div
                    className="text-red text-center fw-900"
                    style={{ fontSize: 22, textShadow: '0 0 12px rgba(255, 0, 0, 0.8)' }}
                >
                    ВЫ ПРОИГРАЛИ
                </div>
            )}
            {phase === 'over' && outcome === 'won' && (
                <div
                    className="text-accent text-center fw-900"
                    style={{ fontSize: 22, textShadow: '0 0 12px rgba(255, 215, 0, 0.8)' }}
                >
                    777! x25 ВАШЕЙ СТАВКИ 🎰
                </div>
            )}

            <button type="button" className="back-btn" onClick={() => onNavigate('games')}>
                НАЗАД В ИГРЫ
            </button>
        </div>
    );
}
